import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { isEqual } from 'lodash-es';
import { useAtomValue } from 'jotai';

import { FormFieldsContainer } from '../../../../../components/Form/FormComponents';
import { dateField, simpleTextField } from '../../../../../data-fields/CommonFields';
import { RendererType } from '../../../../../data-models/field-definition';
import { createFormField } from '../../../../../view-models/form.view-model';
import { FormFieldWithLabelFactory } from '../../../../../components/Form/FormFieldAndLabelFactory';
import { StickyFormButtons } from '../../../../../components/Form/StickyFormButtons';
import {
  ICompanyMilestoneDataMode,
  MilestoneStatus,
} from '../../../../../data-models/company-milestone.data-model';
import { ISelectMeta } from '../../../../../data-models/field3.data-model';
import { ISimpleChoice } from '../../../../../data-models/field2.data-model';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import { useMilestoneActions } from '../../../hooks/useMilestoneActions';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';

export interface MilestoneInitialValues {
  id?: number;
  title: string;
  status: string;
}

interface IMilestoneFormProps {
  onClose: () => void;
  initialValues?: MilestoneInitialValues;
}

interface MilestoneStatusOption {
  id: MilestoneStatus;
  value: MilestoneStatus;
  label: string;
}

const statusOptions: MilestoneStatusOption[] = [
  { id: 'Behind Plan', value: 'Behind Plan', label: 'Behind Plan' },
  { id: 'On Track', value: 'On Track', label: 'On Track' },
  { id: 'Complete', value: 'Complete', label: 'Complete' },
];

export const MilestoneForm: FC<IMilestoneFormProps> = ({ onClose, initialValues }) => {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const defaultVals = initialValues;
  const { handleCreateMilestone, handleEditMilestone } = useMilestoneActions();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  const methods = useForm({
    defaultValues: defaultVals,
    mode: 'onTouched',
  });

  const selectStatusField = createFormField<ISelectMeta<string>>({
    key: 'status',
    label: 'Status',
    required: true,
    dataType: 'string',
    renderer: RendererType.singleSelect,
    rendererMeta: {
      values: statusOptions.reduce((res, statusOp) => {
        res.push({ value: statusOp.id, displayName: statusOp.label });
        return res;
      }, [] as ISimpleChoice<string>[]),
      multi: false,
    },
  });

  const onSubmit = async (values: MilestoneInitialValues) => {
    const payload = { ...values, companyId: companyId };

    const action = typeof initialValues?.id === 'number' ? 'edit' : 'add';

    if (action === 'edit' && isEqual(initialValues, values)) {
      onClose();
      return;
    }

    try {
      if (action === 'add') {
        await handleCreateMilestone(payload as Partial<ICompanyMilestoneDataMode>);
      } else {
        await handleEditMilestone(payload as Partial<ICompanyMilestoneDataMode>);
      }
      pushSuccessToast({ message: `Milestone ${action}ed successfully` });
    } catch (err) {
      pushErrorToast({ message: `Failed to ${action} a milestone` });
    } finally {
      onClose();
    }
  };

  return (
    <FormProvider {...methods}>
      <FormFieldsContainer>
        <FormFieldWithLabelFactory
          formField={simpleTextField({
            key: 'title',
            label: 'Title',
            required: true,
          })}
        />
        <FormFieldWithLabelFactory formField={selectStatusField} />
        <FormFieldWithLabelFactory
          formField={dateField({ key: 'targetDate', label: 'Target Date', required: true })}
        />
      </FormFieldsContainer>
      <StickyFormButtons
        onCancel={onClose}
        onSubmit={() => methods.handleSubmit(onSubmit)()}
        disabled={methods.formState.isSubmitting || !methods.formState.isValid || !methods.formState.isDirty}
        loading={methods.formState.isSubmitting}
        submitLabel={`${typeof initialValues?.id === 'number' ? 'Edit' : 'Add'} Milestone`}
      />
    </FormProvider>
  );
};
